import { create } from 'zustand';
import { devtools, persist } from 'zustand/middleware';
import { fetchProfileAPI, type UserProfile } from '@/services/api/authentication';


interface UserStore {
  accessToken: string | null;
  refreshToken: string | null;
  profile: UserProfile | null;

  _profileLoading: boolean;

  getProfile: () => Promise<UserProfile | null>;
  refreshProfile: (force?: boolean) => Promise<UserProfile | null>;
  setTokens: (access: string, refresh: string) => void;
  clearTokens: () => void;
  setProfile: (profile: UserProfile) => void;
  logout: () => void;
}

const useUserStore = create<UserStore>()(
  devtools(
    persist(
      (set, get) => ({
        accessToken: null,
        refreshToken: null,
        profile: null,

        _profileLoading: false,

        setTokens: (access, refresh) =>
          set((state) => {
            if (typeof window !== 'undefined') {
              localStorage.setItem('token', access);
              localStorage.setItem('refresh_token', refresh);
            }
            return { ...state, accessToken: access, refreshToken: refresh };
          }),

        clearTokens: () =>
          set((state) => {
            if (typeof window !== 'undefined') {
              localStorage.removeItem('token');
              localStorage.removeItem('refresh_token');
            }
            return { ...state, accessToken: null, refreshToken: null };
          }),

        setProfile: (profile) => set((state) => ({ ...state, profile })),

        getProfile: async () => {
          const { profile, _profileLoading } = get();

          if (profile) return profile;

          if (_profileLoading) return profile;

          try {
            set({ _profileLoading: true });
            const { data } = await fetchProfileAPI();
            set({ profile: data });
            return data;
          } catch (err) {
            console.error('Failed to fetch profile:', err);
            return null;
          } finally {
            set({ _profileLoading: false });
          }
        },

        refreshProfile: async (force = false) => {
          const { profile, _profileLoading } = get();

          if (_profileLoading) return profile;

          // Don't refresh if profile exists and force is false
          if (profile && !force) return profile;

          try {
            set({ _profileLoading: true });
            const { data } = await fetchProfileAPI();
            set({ profile: data });
            return data;
          } catch (err) {
            console.error('Failed to refresh profile:', err);
            return profile; // Return existing profile on error
          } finally {
            set({ _profileLoading: false });
          }
        },

        logout: () =>
          set(() => {
            if (typeof window !== 'undefined') {
              localStorage.removeItem('token');
              localStorage.removeItem('refresh_token');
            }
            return {
              accessToken: null,
              refreshToken: null,
              profile: null,
              setTokens: () => {},
              clearTokens: () => {},
              setProfile: () => {},
              logout: () => {},
              _profileLoading: false,
            };
          }),
      }),
      {
        name: 'user-storage', // localStorage key
        partialize: (state) => ({
          accessToken: state.accessToken,
          refreshToken: state.refreshToken,
          profile: state.profile,
        }),
      }
    )
  )
);

// Utility functions
const useIsEmergencyContact = () => {
  const profile = useUserStore((state) => state.profile);
  return profile?.user_type === 'EMR';
};

const useCanCreateEdit = () => {
  const profile = useUserStore((state) => state.profile);
  return profile?.user_type !== 'EMR';
};

export {
  type UserStore,
  useUserStore,
  useIsEmergencyContact,
  useCanCreateEdit,
}
